import { InputNumber, Select, Tooltip } from "antd";
import { useRef, useState } from "react";
import {
  PrintOptionCopyType,
  PrintOptionItem,
} from "shared/types/printOptions";
import styles from "./CopiesField.module.scss";

type Props = {
  printOption: PrintOptionItem;
  value?: number;
  onChange: (value: number) => void;
  onBlur?: () => void;
  onPressEnter?: () => void;
};

const communiCorpEmail = `AflacServiceExcellence@Communicorp.com`;

const TooltipMessage = () => (
  <div className={styles.tooltip}>
    <span>We&apos;ve adjusted the number of copies to the allowed range.</span>
    <span>
      For pricing on quantities outside of this range, contact{" "}
      <a target="_blank" rel="noreferrer" href={`mailto:${communiCorpEmail}`}>
        {communiCorpEmail}
      </a>{" "}
    </span>
  </div>
);

const CopiesField = ({
  printOption,
  value,
  onChange,
  onBlur,
  onPressEnter,
}: Props) => {
  const rawTextRef = useRef<string | null>(null);
  const tooltipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  if (!onChange) return null;
  const { costPerCopy, copySelection } = printOption;

  if (copySelection === PrintOptionCopyType.RANGE) {
    const min = costPerCopy[0]?.quantity?.min ?? 1;
    const max = costPerCopy[costPerCopy.length - 1]?.quantity?.max;
    const isNumericInput = (text: string) => /^-?\d*\.?\d*$/.test(text);

    const clearTooltipTimer = () => {
      if (tooltipTimerRef.current) {
        clearTimeout(tooltipTimerRef.current);
        tooltipTimerRef.current = null;
      }
    };

    const hideTooltip = () => {
      setShowTooltip(false);
      clearTooltipTimer();
    };

    const scheduleTooltipHide = () => {
      clearTooltipTimer();
      tooltipTimerRef.current = setTimeout(() => {
        setShowTooltip(false);
        tooltipTimerRef.current = null;
      }, 10000);
    };

    const handleInput = (text: string) => {
      rawTextRef.current = isNumericInput(text) ? text : null;
      hideTooltip();
    };

    const clampToRange = (value: number, min: number, max?: number) => {
      if (value < min) return min;
      if (typeof max !== "undefined" && value > max) return max;

      return value;
    };

    const handleBlur = (
      e:
        | React.FocusEvent<HTMLInputElement>
        | React.KeyboardEvent<HTMLInputElement>,
    ) => {
      const raw = e.currentTarget.value;
      const parsed = parseFloat(raw);

      if (!isNaN(parsed)) {
        const clamped = clampToRange(parsed, min, max);

        if (parsed !== clamped) {
          setShowTooltip(true);
          scheduleTooltipHide();
        }
      }

      if (e.type === "blur") onBlur?.();

      if (e.type === "keydown" && (e as React.KeyboardEvent).key === "Enter") {
        onPressEnter?.();
      }
    };

    return (
      <Tooltip
        overlayInnerStyle={{ minWidth: 380 }}
        title={TooltipMessage}
        visible={showTooltip}
        placement="top"
      >
        <InputNumber
          min={min}
          max={max}
          value={value}
          onChange={onChange}
          onInput={handleInput}
          onBlur={handleBlur}
          onPressEnter={handleBlur}
        />
      </Tooltip>
    );
  }

  return (
    <Select
      value={value}
      onChange={onChange}
      options={costPerCopy.map(c => ({
        label: c.quantity.toString(),
        value: c.quantity,
      }))}
    />
  );
};

export default CopiesField;
